import * as Yup from 'yup';
import { SxProps, Theme } from '@mui/material';
import { InfoTypeResponseDto } from '@/gen/api/securefile';

// ===== Schemas =====

export const topSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters')
    .test('is-valid-chars', 'Name can only contain letters, numbers, underscores(_), or hyphens(-).', (x: string) => {
      return /^[a-zA-Z0-9_-]+$/.test(x);
    }),
  displayName: Yup.string().required('Display Name is required').max(100, 'Display Name must be at most 100 characters'),
  description: Yup.string().max(512, 'Description must be at most 512 characters'),
});

export const dpSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
  score: Yup.number().required('Score is required'),
  regex: Yup.string().required('Regular Expression is required')
    .test('is-valid-regex', 'Regular Expression value is incorrect', (x: string) => {
      try {
        new RegExp(x);
        return true;
      } catch {
        return false;
      }
    }),
});

export const krSchema = Yup.object({
  regex: Yup.string().nullable().test('is-valid-regex', 'Invalid regular expression', (x: string) => {
    try {
      x && new RegExp(x);
      return true;
    } catch {
      return false;
    }
  }),
  words: Yup.array(Yup.string()).nullable(),
  windowBefore: Yup.number().required('Required'),
  windowAfter: Yup.number().required('Required'),
  scoreAdjustment: Yup.number().required('Required'),
});

export const erSchema = Yup.object({
  regex: Yup.string().nullable().test('is-valid-regex', 'Invalid regular expression', (x: string) => {
    try {
      x && new RegExp(x);
      return true;
    } catch {
      return false;
    }
  }),
  words: Yup.array(Yup.string()).nullable(),
  excludeInfoTypes: Yup.array(Yup.string()).nullable(),
});

// ===== Types =====

export interface InfoTypeFormState {
  prevData: InfoTypeResponseDto;
  stage: {
    name: string;
    displayName: string;
    description: string;
    isEnabled: boolean;
  };
  denyListCount: number;
  detectPatternCount: number;
  countTouched: boolean;
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  dirtySet: Set<string>;
}

export const emptyInfoTypeDto = Object.freeze({
  name: '',
  displayName: '',
  description: '',
  isEnabled: true,
  detectPatterns: Object.freeze([]) as ReadonlyArray<unknown>,
  denyList: Object.freeze([]) as ReadonlyArray<unknown>,
  keywordRules: Object.freeze([]) as ReadonlyArray<unknown>,
  exclusionRules: Object.freeze([]) as ReadonlyArray<unknown>,
} as InfoTypeResponseDto);

export const initialVal = (record: InfoTypeResponseDto) => ({
  prevData: record,
  stage: {
    name: record.name,
    displayName: record.displayName,
    description: record.description,
    isEnabled: record.isEnabled,
  },
  denyListCount: record.denyList?.length ?? 0,
  detectPatternCount: record.detectPatterns?.length ?? 0,
  countTouched: false,
  errors: {} as Record<string, unknown>,
  touched: {} as Record<string, unknown>,
  dirtySet: new Set<string>(),
});

// ===== Shared Styles =====

export const EditorLineSx: SxProps<Theme> = {
  '& li.MuiListItem-root': {
    pl: 3,
    '&:hover': {
      bgcolor: theme => theme.palette.action.hover,
    },
    '&::before': {
      display: 'block',
      content: "''",
      position: 'absolute',
      left: 0,
      height: 'calc(100% - 2px)',
      width: 4,
      bgcolor: 'divider',
    },
  },
  '& li.MuiListItem-root.added::before': { bgcolor: 'success.light' },
  '& li.MuiListItem-root.removed': {
    '&::before': { bgcolor: 'error.light' },
    '& input, & textarea': { textDecoration: 'line-through' },
  },
  '& li.MuiListItem-root.dirty::before': { bgcolor: 'info.light' },
};
